// src/pages/chat/chat.tsx
/** @jsxImportSource @emotion/react */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, easeOut, easeIn } from 'framer-motion';
import { ChatWrapper, ChatBody, ChatMain, TopThird, MiddleThird, BottomThird } from './chat.styled';
import { useChat } from './chat.hooks';
import { WelcomeContent } from './welcome-content/welcome-content';
import { Dock } from './dock/dock';
import type { DockHandle } from './dock/dock';
import { NewMessage } from './new-message/new-message';
import { Header } from './header/header';
import FullscreenWaveform from './recording-orb/recording-orb';
import { AttachmentPreviewModal } from './dock/AttachmentPreviewModal';
import { RecordDock } from './dock/record-dock/record-dock';
import type { RecordDockHandle } from './dock/record-dock/record-dock';
import type { CtaHandle, Phase } from './CTA/cta';
import { FullScreenRecordingDock } from './fullscreen-recording-dock/fullscreen-recording-dock';
import { useDockMode } from './dock-mode.hooks';
import { stopFromFullscreen } from './stop-fullscreen';
import { Conversation } from './conversation/conversation';
import { useConversation } from './conversation/conversation.hooks';
import { ENVIRONMENT } from '../../env';
import { userNotesApi } from '../../api/notes/notes';
import { invalidateNotesCache } from './sidebar/sidebar.hooks';
import { useGlobalNotifications } from '../../hooks/use-global-notifications';
import { getUserId } from '../../api/chat/chat';
import { useAppBoot } from '../../contexts/app-boot-context';
import { useNotifications } from '../../contexts/notification-context';
import { getUserFriendlyErrorMessage } from '../../utils/error-messages';
import { FILE_ACCEPT, MAX_TOTAL_FILE_SIZE, validateFiles, formatFileSize } from '../../constants/upload-limits';
import { TrialBanner } from '../../components/trial-banner/trial-banner';
import { useBackHandler } from '../../platform/use-back-handler';
import {
  ModalOverlay,
  ModalContent,
  RerecordConfirmation,
  DiscardAndReRecordTitleSpan,
  DiscardAndReRecordDescriptionSpan,
  CTAWrapper,
  CancelButton as ModalCancelButton,
  ReRecordButton,
} from './dock/record-dock/record-dock.styled';
import { usePromptQueue } from '../../contexts/prompt-queue-context';
import { useIsDesktop } from '../../hooks/use-is-desktop';
import Sidebar from './sidebar/sidebar';

const DEV = ENVIRONMENT === 'DEV';
const log = (...args: any[]) => {
  if (DEV) console.log('[chat.tsx]', ...args);
};

const exitToBottom = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.26, ease: easeOut } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: easeIn } },
};

// Slide from below the overflow boundary so layout shifts are invisible
const dockSlide = {
  initial: { y: '100%' },
  animate: { y: 0, transition: { type: 'spring' as const, stiffness: 380, damping: 36 } },
  exit: { y: '100%', transition: { type: 'spring' as const, stiffness: 380, damping: 36 } },
};

// Conversation slides up from below + fades in (like native forward navigation)
const conversationEnter = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.22, ease: [0.4, 0, 0.7, 0.2] as const },
  },
};

// Welcome content fades in gently when returning from conversation
const welcomeEnter = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: [0.4, 0, 1, 1] as const },
  },
};


function formatNewMessageDate(v: unknown): string {
  const d = v instanceof Date ? v : new Date(String(v ?? ''));
  const date = isNaN(d.getTime()) ? new Date() : d;

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString(undefined, { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);

  return `${day} ${month} ${year}`;
}

const enterWave = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.4, ease: [0.4, 0, 1, 1] as const } },
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export const Chat: React.FC = () => {
  const location = useLocation();
  const { handleNotificationClick, isFullScreenRecordingMode, setIsFullScreenRecordingMode } =
    useChat();
  const isDesktop = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const toggleSidebar = React.useCallback(() => setSidebarOpen((o) => !o), []);
  const [avatarSettingsTab, setAvatarSettingsTab] = React.useState<string | null>(null);

  // ── Expose sidebar width as CSS variable for fixed-position modals ──
  React.useEffect(() => {
    const width = isDesktop ? (sidebarOpen ? '300px' : '56px') : '0px';
    document.documentElement.style.setProperty('--sidebar-width', width);
    return () => { document.documentElement.style.removeProperty('--sidebar-width'); };
  }, [isDesktop, sidebarOpen]);

  const { latestMessage: preloadedLatestMessage, refreshLatestMessage } = useAppBoot();

  const { latestRealtimeEvent } = useNotifications();

  // ── Permission prompt queue: fire triggers when chat mounts ──
  const { fireEvent } = usePromptQueue();
  const promptFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (promptFiredRef.current) return;
    promptFiredRef.current = true;

    const justFinishedOnboarding =
      sessionStorage.getItem('mailander_just_finished_onboarding') === 'true';
    if (justFinishedOnboarding) {
      sessionStorage.removeItem('mailander_just_finished_onboarding');
      fireEvent('onboarding_to_chat');
    } else {
      // Normal app open — catches time-based re-prompts (attempts 3 & 4)
      fireEvent('app_first_open');
    }
  }, [fireEvent]);

  // ── Auto-play wiring (notification panel clicks) ──
  const [autoPlayMessageId, setAutoPlayMessageId] = React.useState<string | null>(null);

  // ── Scroll-to-message: triggered from notification panel clicks ──
  const [scrollToMessageId, setScrollToMessageId] = React.useState<string | null>(null);
  const [searchHighlightQuery, setSearchHighlightQuery] = React.useState<string | null>(null);
  const handleScrollHandled = React.useCallback(() => {
    setScrollToMessageId(null);
    // NOTE: do NOT unsuppress scrollToBottom here — the scroll-to-message
    // flow in conversation.tsx controls the timing via scrollToActiveRef.
    // Unsuppressing here would let scrollToBottom fire via rAF and jump to bottom.
  }, []);


  const { showSuccess, showError } = useGlobalNotifications();

  const ctaRef = React.useRef<CtaHandle | null>(null);
  const dockRef = React.useRef<DockHandle | null>(null);
  const recordDockRef = React.useRef<RecordDockHandle | null>(null);

  const [ctaPhase, setCtaPhase] = React.useState<Phase>('restLoop');
  const isPaused = ctaPhase === 'paused';

  const [showAttachmentPreviewModal, setShowAttachmentPreviewModal] = React.useState(false);
  const [attachmentData, setAttachmentData] = React.useState<{
    type: 'file' | 'photo' | 'link';
    data: File | File[] | string;
  } | null>(null);

  const {
    dockMode,
    isFullscreen,
    openRecordDock,
    closeRecordDock,
    enterFullscreen,
    exitFullscreen,
  } = useDockMode({
    isFullScreenRecordingMode,
    setIsFullScreenRecordingMode,
  });

  React.useEffect(() => {
    log('dockMode =', dockMode, '| isFullScreenRecordingMode =', isFullScreenRecordingMode);
  }, [dockMode, isFullScreenRecordingMode]);

  React.useEffect(() => {
    log('ctaPhase =', ctaPhase);
  }, [ctaPhase]);

  const isRecordingPhase =
    ctaPhase === 'recording' ||
    ctaPhase === 'toRecording' ||
    ctaPhase === 'paused' ||
    ctaPhase === 'toResume' ||
    ctaPhase === 'toPaused';

  const shouldMountWaveform =
    (dockMode === 'fullscreen' || isRecordingPhase) && isRecordingPhase;

  const isWaveformListening =
    (dockMode === 'fullscreen' || isRecordingPhase) &&
    (ctaPhase === 'recording' ||
      ctaPhase === 'toRecording' ||
      ctaPhase === 'toResume' ||
      ctaPhase === 'toPaused');

  const [_waveKey, setWaveKey] = React.useState(0);
  const prevMountRef = React.useRef(false);

  React.useEffect(() => {
    const prev = prevMountRef.current;
    prevMountRef.current = shouldMountWaveform;

    // bump key ONLY on first mount edge
    if (!prev && shouldMountWaveform) {
      setWaveKey((k) => k + 1);
    }
  }, [shouldMountWaveform]);

  const [morph, setMorph] = React.useState(isWaveformListening ? 1 : 0);

  React.useEffect(() => {
    if (DEV) log('isWaveformListening ->', isWaveformListening);
  }, [isWaveformListening]);

  React.useEffect(() => {
    const from = morph;
    const to = isWaveformListening ? 1 : 0;

    const duration = 600;
    const start = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const t = clamp01((now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setMorph(lerp(from, to, eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaveformListening]);

  const waveformProps = React.useMemo(() => {
    const t = morph;

    return {
      listening: isWaveformListening,
      backgroundColor: '#070a12',
      color: '#2a66ff',

      motion: t,
      idleStrength: t,

      layers: Math.round(lerp(1, 4, t)),
      smoothing: 0.72,
      gain: lerp(0, 2.1, t),
      frequency: lerp(0, 1.1, t),
      waveCount: lerp(0, 1.5, t),
      speed: lerp(0, 1, t),
      pulseRate: lerp(0, 6, t),
      minAmplitude: lerp(0, 0.01, t),

      blendMode: 'screen' as const,
      shadowBlur: 12,
      useGradient: true,

      variation: {
        jitterScale: lerp(0, 2, t),
        warp: lerp(0, 88, t),
        octaves: Math.round(lerp(0, 8, t)),
        perLayerFreqJitter: 0.22,
        perLayerPhaseDrift: 0.2,
        verticalWobble: 0.2,
      },
    };
  }, [morph, isWaveformListening]);

  const {
    conversationId,
    messages,
    isLoading,
    isBootstrapping,
    wsConnected,
    sendMessage,
    retryMessage,
    cancelUpload,
    notifyConversationOpened,
    loadOlderMessages,
    hasMore,
    isLoadingOlder,
    loadMessagesAround,
    loadNewerMessages,
    hasMoreNewer,
    isLoadingNewer,
    clearMessages,
    markFileDeleted,
    reloadMessages,
  } = useConversation();

  // ── Refresh messages when a new notification arrives via WS ──
  React.useEffect(() => {
    if (latestRealtimeEvent?.event_type === 'message_received') {
      refreshLatestMessage();
      reloadMessages();
    }
  }, [latestRealtimeEvent, refreshLatestMessage, reloadMessages]);

  // ── Fallback poll: catch scheduled messages the WS broadcast missed ──
  React.useEffect(() => {
    const interval = setInterval(() => {
      reloadMessages();
    }, 15_000);
    return () => clearInterval(interval);
  }, [reloadMessages]);

  // ✅ conversation should render when explicitly opened (not in fullscreen)
  // Also consider preloaded data from boot context
  const hasConversation =
    (!isBootstrapping && (!!conversationId || messages.length > 0)) || !!preloadedLatestMessage;

  // ✅ User intent (tap NewMessage / push notification) controls opening conversation.
  // Initialize to true when arriving via deep link (/chat/{id}) so the conversation
  // opens immediately on mount — no flash of the welcome screen.
  const hasDeepLinkOnMount = location.pathname.startsWith('/chat/') && location.pathname.length > '/chat/'.length;
  const hasPushTapOnMount = !!(location.state as any)?.pushTap;
  const [isConversationOpen, setIsConversationOpen] = React.useState(hasDeepLinkOnMount || hasPushTapOnMount);

  // Track pending deep link so conversation view shows immediately (even during bootstrap)
  const [pendingDeepLink, setPendingDeepLink] = React.useState<string | null>(
    hasDeepLinkOnMount ? location.pathname : null,
  );

  // ✅ keep CTA mounted during stop->transcribe->send so Conversation can open immediately
  const [keepWelcomeMounted, setKeepWelcomeMounted] = React.useState(false);

  // ✅ Single source of truth: scroll the conversation List to its bottom.
  // All calls coalesce into a single rAF so only one scroll happens per paint frame.
  const chatMainRef = React.useRef<HTMLElement>(null);
  const scrollRafRef = React.useRef(0);
  const suppressScrollToBottomRef = React.useRef(false);
  const pendingScrollTimersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  const scrollToBottom = React.useCallback(() => {
    if (suppressScrollToBottomRef.current) return;
    cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      if (suppressScrollToBottomRef.current) return;
      // Scroll the conversation List (the actual scroll container) to bottom
      const list = chatMainRef.current?.querySelector('[aria-label="Conversation"]');
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    });
  }, []);
  const requestScrollToBottom = React.useCallback(() => {
    // Cancel any pending scroll timers before scheduling new ones
    pendingScrollTimersRef.current.forEach(clearTimeout);
    pendingScrollTimersRef.current = [];
    // Immediate + two retries to cover AnimatePresence mount (~460ms)
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 150);
    const t2 = setTimeout(scrollToBottom, 500);
    pendingScrollTimersRef.current = [t1, t2];
  }, [scrollToBottom]);
  // Cancel pending scroll-to-bottom timers (called by Conversation when user loads older messages)
  const cancelPendingScrollToBottom = React.useCallback(() => {
    suppressScrollToBottomRef.current = true;
    cancelAnimationFrame(scrollRafRef.current);
    pendingScrollTimersRef.current.forEach(clearTimeout);
    pendingScrollTimersRef.current = [];
  }, []);
  const unsuppressScrollToBottom = React.useCallback(() => {
    suppressScrollToBottomRef.current = false;
  }, []);

  // Auto-open conversation when arriving from a push notification deep link (e.g. /chat/{conversationId}).
  // Depends on location.pathname AND location.state so it fires both on initial mount AND when
  // navigate('/chat/{id}') is called while Chat is already mounted (e.g. notification tap).
  // The state dependency ensures re-triggers even if the pathname hasn't changed.
  React.useEffect(() => {
    const isDeepLink = location.pathname.startsWith('/chat/') && location.pathname.length > '/chat/'.length;
    const isPushTap = !!(location.state as any)?.pushTap;

    if (isDeepLink || isPushTap) {
      setIsConversationOpen(true);
      if (isDeepLink) setPendingDeepLink(location.pathname);
      requestScrollToBottom();
      // Clean URL so back-button / re-render doesn't re-trigger
      if (isDeepLink) window.history.replaceState(null, '', '/chat');
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear pending deep link once bootstrap finishes AND conversation data is available.
  // Wait for hasConversation to prevent a flash where showConversation briefly becomes
  // false between pendingDeepLink clearing and conversationId arriving.
  React.useEffect(() => {
    if (!isBootstrapping && pendingDeepLink && hasConversation) {
      setPendingDeepLink(null);
    }
  }, [isBootstrapping, pendingDeepLink, hasConversation]);

  const showConversation = (hasConversation || !!pendingDeepLink) && !isFullscreen && isConversationOpen;

  // ✅ When conversation becomes visible, mark seen via websocket
  React.useEffect(() => {
    if (showConversation) {
      notifyConversationOpened();
    }
  }, [showConversation, notifyConversationOpened]);

  const latestMessage = React.useMemo(() => {
    if (messages && messages.length > 0) return messages[messages.length - 1] ?? null;
    // Fall back to preloaded message from boot context
    if (preloadedLatestMessage) return preloadedLatestMessage;
    return null;
  }, [messages, preloadedLatestMessage]);

  // Resolve pending auto-play to the actual latest message ID
  React.useEffect(() => {
    if (autoPlayMessageId !== '__pending__') return;
    if (!latestMessage) return;
    const id = String((latestMessage as any).ui_id ?? (latestMessage as any).id ?? '');
    if (id) {
      setAutoPlayMessageId(id);
    } else {
      setAutoPlayMessageId(null);
    }
  }, [autoPlayMessageId, latestMessage]);

  const handleAutoPlayHandled = React.useCallback(() => {
    setAutoPlayMessageId(null);
  }, []);

  const isLatestMessageFromMe = React.useMemo(() => {
    if (!latestMessage) return false;
    const currentUserId = getUserId();
    return latestMessage.sender_id === currentUserId;
  }, [latestMessage]);

  const latestPreviewText = String(latestMessage?.content ?? '').trim() || 'Open your conversation';

  const latestAudioUrl = React.useMemo(() => {
    if (!latestMessage) return null;
    return (latestMessage as any).ui_audio_url ?? (latestMessage as any).audio_url ?? null;
  }, [latestMessage]);

  const latestDateLabel = latestMessage
    ? formatNewMessageDate((latestMessage as any).updated_at ?? latestMessage.created_at)
    : '';

  // Debug logging
  React.useEffect(() => {
    if (DEV && latestMessage) {
      log('latestMessage:', latestMessage);
      log('created_at:', latestMessage.created_at);
      log('formatted date:', latestDateLabel);
    }
  }, [latestMessage, latestDateLabel]);

  // =========================
  // Swipe-down close (drag ONLY on fullscreen dock)
  // =========================

  // ✅ Open conversation from notification panel click, optionally scroll to a specific message
  const openConversationFromNotification = React.useCallback(
    (messageId?: string) => {
      setIsConversationOpen(true);
      requestScrollToBottom();
      if (isFullscreen) exitFullscreen();
      if (messageId) {
        setScrollToMessageId(messageId);
      }
    },
    [isFullscreen, exitFullscreen, requestScrollToBottom]
  );

  // ✅ Open conversation from notification listen pill — opens conversation + auto-plays the specific message
  const openConversationAndPlayFromNotification = React.useCallback(
    (messageId: string) => {
      setIsConversationOpen(true);
      requestScrollToBottom();
      if (isFullscreen) exitFullscreen();
      setAutoPlayMessageId(messageId);
    },
    [isFullscreen, exitFullscreen, requestScrollToBottom]
  );

  // ✅ NEW: clicking NewMessage opens conversation (and exits fullscreen if needed)
  const openConversationFromNewMessage = React.useCallback(() => {
    setIsConversationOpen(true);
    requestScrollToBottom();
    if (isFullscreen) exitFullscreen();
  }, [isFullscreen, exitFullscreen, requestScrollToBottom]);

  // ✅ Listen from NewMessage: open conversation + auto-play the latest message
  const listenFromNewMessage = React.useCallback(() => {
    setIsConversationOpen(true);
    if (isFullscreen) exitFullscreen();
    setAutoPlayMessageId('__pending__');
  }, [isFullscreen, exitFullscreen]);

  // NEW: back button closes conversation and dismisses type dock
  const handleBackFromConversation = React.useCallback(() => {
    if (dockMode === 'record') {
      recordDockRef.current?.requestClose(() => {
        setIsConversationOpen(false);
      });
      return;
    }
    setIsConversationOpen(false);
    dockRef.current?.closeType();
  }, [dockMode]);

  useBackHandler('conversation', showConversation, handleBackFromConversation);

  // ✅ Stop in fullscreen: exit fullscreen and open conversation immediately
  // so the user never sees the home screen in between.
  const handleStopFromFullscreenDock = React.useCallback(() => {
    stopFromFullscreen({
      exitFullscreen,
      setIsConversationOpen,
      setKeepWelcomeMounted,
      requestScrollToBottom,
      ctaStop: () => ctaRef.current?.stop(),
    });
  }, [exitFullscreen, requestScrollToBottom]);

  const handleCancelFromFullscreen = React.useCallback(() => {
    setKeepWelcomeMounted(false);
    ctaRef.current?.cancel?.();
    exitFullscreen();
  }, [exitFullscreen]);

  // "View in conversation" from files drawer / sidebar search
  const handleViewFileInConversation = React.useCallback(
    async (messageId: string, _conversationId: string, searchQuery?: string) => {
      // Set scroll target BEFORE loading messages so the mount effect
      // sees it and skips scrollToBottom() — prevents the "jumps to
      // bottom first" flash.
      // Clear old messages BEFORE showing conversation so user sees
      // only the loading spinner, never stale content at scrollTop=0.
      clearMessages();
      setScrollToMessageId(messageId);
      setSearchHighlightQuery(searchQuery ?? null);
      suppressScrollToBottomRef.current = true;
      cancelPendingScrollToBottom();
      setIsConversationOpen(true);

      const targetId = await loadMessagesAround(_conversationId, messageId);
      if (targetId) {
        setScrollToMessageId(targetId);
      }
    },
    [clearMessages, cancelPendingScrollToBottom, loadMessagesAround],
  );

  // ✅ Sending guard — prevents double-send from concurrent send paths
  const submittingRef = React.useRef(false);
  const [isSending, setIsSending] = React.useState(false);

  // ✅ beforeunload + pagehide protection during active sends
  React.useEffect(() => {
    if (!isSending) return;
    const warn = (e: Event) => {
      e.preventDefault();
      (e as any).returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('pagehide', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('pagehide', warn);
    };
  }, [isSending]);

  // ✅ Recording safety: discard confirmation modal
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);

  const isActivelyRecording = isFullscreen && (ctaPhase === 'recording' || ctaPhase === 'toResume');
  const isRecordingPaused = isFullscreen && (ctaPhase === 'paused' || ctaPhase === 'toPaused');

  // Navigation guard: block browser back button during active recording
  const isRecordingInProgress = dockMode === 'record' || dockMode === 'fullscreen';
  const isRecordingInProgressRef = React.useRef(isRecordingInProgress);
  isRecordingInProgressRef.current = isRecordingInProgress;

  React.useEffect(() => {
    if (!isRecordingInProgress) return;
    // Push a dummy history entry so "back" pops it instead of leaving the page
    window.history.pushState({ recordingGuard: true }, '');

    const handlePopState = (_e: PopStateEvent) => {
      if (isRecordingInProgressRef.current) {
        // Re-push so back button can be caught again
        window.history.pushState({ recordingGuard: true }, '');
        setShowDiscardModal(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isRecordingInProgress]);

  const handleMenuBlocked = React.useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  const handleDiscardCancel = React.useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleDiscardConfirm = React.useCallback(() => {
    setShowDiscardModal(false);
    setKeepWelcomeMounted(false);
    ctaRef.current?.cancel?.();
    exitFullscreen();
  }, [exitFullscreen]);

  const handleRecordingFailed = React.useCallback(
    (message: string) => {
      showError(message);
    },
    [showError]
  );

  // ✅ CTA stop -> transcribe -> send as message (progress is now per-message inline)
  const handleCtaSendRecording = React.useCallback(
    async (audioBlob: Blob, transcript?: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSending(true);

      const text = String(transcript ?? '').trim();

      // Validate audio blob — skip sending empty/tiny audio (< 1KB = container only)
      const hasValidAudio = audioBlob.size > 1024;

      // Nothing to send: no valid audio and no text
      if (!hasValidAudio && !text) {
        showError('Recording was too short. Please try again.');
        submittingRef.current = false;
        setIsSending(false);
        return;
      }

      // ✅ Open conversation instantly — don't block on audio decoding
      setIsConversationOpen(true);
      requestScrollToBottom();
      if (isFullscreen) exitFullscreen();

      let audioFile: File | undefined;
      let audioDuration: number | undefined;

      if (hasValidAudio) {
        const ext = (audioBlob.type || '').includes('mp4')
          ? 'mp4'
          : (audioBlob.type || '').includes('ogg')
            ? 'ogg'
            : 'webm';

        audioFile = new File([audioBlob], `recording.${ext}`, {
          type: audioBlob.type || 'audio/webm',
        });

        // Calculate audio duration for backend
        let _audioCtx: AudioContext | undefined;
        try {
          const AudioCtx = (window.AudioContext ||
            (window as any).webkitAudioContext) as typeof AudioContext;
          _audioCtx = new AudioCtx();
          if (_audioCtx.state === 'suspended') await _audioCtx.resume();
          const buf = await audioBlob.arrayBuffer();
          const decoded = await _audioCtx.decodeAudioData(buf);
          audioDuration = Math.round(decoded.duration);
        } catch {
        } finally {
          _audioCtx?.close().catch(() => {});
        }
      }

      try {
        await sendMessage(text, undefined, audioFile, false, {
          ...(hasValidAudio && !text ? { transcribePending: true } : {}),
          ...(audioDuration != null && { audioDuration }),
        });
        setKeepWelcomeMounted(false);
      } catch (e) {
        showError(getUserFriendlyErrorMessage(e));
      } finally {
        submittingRef.current = false;
        setIsSending(false);
      }
    },
    [isFullscreen, exitFullscreen, sendMessage, showError, requestScrollToBottom]
  );

  // No layout changes during recording — the fixed-position overlays
  // (waveform z:1000, dock z:1001) cover the screen. TopThird/BottomThird
  // stay mounted to keep the CTA in exactly the same pixel position.


  const [fsRenderTick, setFsRenderTick] = React.useState(0);

  React.useEffect(() => {
    if (!isFullscreen) return;

    const t = window.setTimeout(() => {
      setFsRenderTick((k) => k + 1);
    }, 120);

    return () => window.clearTimeout(t);
  }, [isFullscreen]);

  return (
    <ChatWrapper>
      {isDesktop && (
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpenUrgent={() => {}}
          isDesktop
          onToggle={toggleSidebar}
          openSettingsTab={avatarSettingsTab}
          onSettingsClosed={() => setAvatarSettingsTab(null)}
          onOpenConversation={() => {
            setIsConversationOpen(true);
            requestScrollToBottom();
          }}
          onViewFileInConversation={handleViewFileInConversation}
          onFileDeleted={markFileDeleted}
        />
      )}
      <ChatBody>
      <TrialBanner />
      <Header
        onNotificationClick={() => {
          handleNotificationClick();
        }}
        showBackButton={showConversation}
        onBackNavigate={handleBackFromConversation}
        showCancelButton={isRecordingPhase}
        onCancel={handleCancelFromFullscreen}
        isRecordingActive={isActivelyRecording}
        onMenuBlocked={isRecordingPaused ? handleMenuBlocked : undefined}
        onOpenConversation={openConversationFromNotification}
        onListenInConversation={openConversationAndPlayFromNotification}
        disableSidebar={isDesktop}
        isDesktop={isDesktop}
        onToggleSidebar={toggleSidebar}
        onAvatarClick={isDesktop ? () => setAvatarSettingsTab('profile') : undefined}
        conversationId={conversationId ?? undefined}
        onViewFileInConversation={handleViewFileInConversation}
        onFileDeleted={markFileDeleted}
      />

      <ChatMain ref={chatMainRef} style={{ overflowY: isFullscreen || isRecordingPhase ? 'hidden' : 'auto' }}>
        <AnimatePresence>
          {shouldMountWaveform && (
            <motion.div
              key={`dock-fullscreen-${fsRenderTick}`}
              {...enterWave}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                maxHeight: '50%',
                pointerEvents: 'none',
                overflow: 'hidden',
                filter: 'blur(50px)',
                transform: 'translateZ(0)',
                willChange: 'filter, transform',
                WebkitMaskImage:
                  'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
                maskImage:
                  'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: '100% 100%',
                maskSize: '100% 100%',
                touchAction: 'auto',
                opacity: 1,
              }}
              aria-label="Recording waveform"
              role="region"
            >
              <FullscreenWaveform {...waveformProps} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!showConversation && (
            <motion.div
              key="top-section"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: isRecordingPhase ? 0 : 1, y: 0, transition: { duration: isRecordingPhase ? 0.5 : 0.32, ease: [0.22, 1, 0.36, 1] as const } }}
              exit={{ opacity: 0, y: -12, transition: { duration: 0.26, ease: [0.4, 0, 0.7, 0.2] as const } }}
              style={{ flex: '0 0 auto', overflow: 'hidden', pointerEvents: isRecordingPhase ? 'none' : 'auto', width: '100%' }}
            >
              <TopThird>
                {!!latestMessage && (
                  <AnimatePresence>
                    <motion.div
                      {...exitToBottom}
                      style={{
                        width: '100%',
                        ...(dockMode === 'record' || isSending
                          ? { pointerEvents: 'none' as const, opacity: 0.6 }
                          : {}),
                      }}
                    >
                      <NewMessage
                        previewText={latestPreviewText}
                        dateLabel={latestDateLabel || ' '}
                        onOpenConversation={openConversationFromNewMessage}
                        isFromMe={isLatestMessageFromMe}
                        audioUrl={latestAudioUrl}
                        onListenClick={listenFromNewMessage}
                      />
                    </motion.div>
                  </AnimatePresence>
                )}
              </TopThird>
            </motion.div>
          )}
        </AnimatePresence>

        <MiddleThird
          style={{
            alignItems: showConversation ? 'normal' : 'center',
            paddingTop: '0px',
            position: 'relative',
            transition: 'padding-top 0.28s ease',
          }}
        >
          <AnimatePresence initial={false}>
            {showConversation ? (
              <motion.div
                key="middle-conversation"
                {...conversationEnter}
                style={{ position: 'absolute', inset: 0, width: '100%', willChange: 'opacity, transform' }}
              >
                {hasConversation ? (
                  <Conversation
                    messages={messages}
                    isLoading={isLoading}
                    wsConnected={wsConnected}
                    loadOlderMessages={loadOlderMessages}
                    hasMore={hasMore}
                    isLoadingOlder={isLoadingOlder}
                    autoPlayMessageId={autoPlayMessageId !== '__pending__' ? autoPlayMessageId : null}
                    onAutoPlayHandled={handleAutoPlayHandled}
                    scrollToMessageId={scrollToMessageId}
                    searchHighlightQuery={searchHighlightQuery}
                    onScrollHandled={handleScrollHandled}
                    retryMessage={retryMessage}
                    cancelUpload={cancelUpload}
                    scrollToBottom={scrollToBottom}
                    cancelPendingScrollToBottom={cancelPendingScrollToBottom}
                    unsuppressScrollToBottom={unsuppressScrollToBottom}
                    loadNewerMessages={loadNewerMessages}
                    hasMoreNewer={hasMoreNewer}
                    isLoadingNewer={isLoadingNewer}
                  />
                ) : (
                  /* Deep link pending — show empty dark pane while conversation bootstraps */
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }} />
                )}
              </motion.div>
            ) : (
              <motion.div
                key="middle-welcome"
                {...welcomeEnter}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  willChange: 'opacity, transform',
                  // Block all interaction when dock recording or submitting
                  ...(dockMode === 'record' || isSending
                    ? { pointerEvents: 'none' as const, opacity: 0.6 }
                    : {}),
                }}
              >
                <WelcomeContent
                  isFullScreenRecordingMode={isFullScreenRecordingMode}
                  setIsFullScreenRecordingMode={setIsFullScreenRecordingMode}
                  ctaRef={ctaRef}
                  onCtaPhaseChange={setCtaPhase}
                  onSendRecording={handleCtaSendRecording}
                  onRecordingFailed={handleRecordingFailed}
                  disabled={dockMode === 'record' || isSending}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {showConversation && keepWelcomeMounted && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                opacity: 0,
              }}
              aria-hidden="true"
            >
              <WelcomeContent
                isFullScreenRecordingMode={isFullScreenRecordingMode}
                setIsFullScreenRecordingMode={setIsFullScreenRecordingMode}
                ctaRef={ctaRef}
                onCtaPhaseChange={setCtaPhase}
                onSendRecording={handleCtaSendRecording}
              />
            </div>
          )}
        </MiddleThird>

        <AnimatePresence>
          {!showConversation && (
            <motion.div
              key="bottom-section"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: isRecordingPhase ? 0 : 1, y: 0, transition: { duration: isRecordingPhase ? 0.5 : 0.32, ease: [0.22, 1, 0.36, 1] as const } }}
              exit={{ opacity: 0, y: 12, transition: { duration: 0.26, ease: [0.4, 0, 0.7, 0.2] as const } }}
              style={{ flex: '0 1 auto', minHeight: 0, overflow: 'hidden', pointerEvents: isRecordingPhase ? 'none' : 'auto' }}
            >
              <BottomThird />
            </motion.div>
          )}
        </AnimatePresence>
      </ChatMain>

      <div style={{ position: 'relative' }}>
      <AnimatePresence initial={false} mode="wait">
        {dockMode === 'record' && (
          <motion.div key="dock-record" {...dockSlide}>
            <RecordDock
              ref={recordDockRef}
              onClose={closeRecordDock}
              onSend={async (audioBlob: Blob, files?: File[], transcript?: string) => {
                if (submittingRef.current) return;
                submittingRef.current = true;
                setIsSending(true);

                const text = String(transcript ?? '').trim();

                // Validate audio blob — skip sending empty/tiny audio (< 1KB = container only)
                const hasValidAudio = audioBlob.size > 1024;

                let audioFile: File | undefined;
                let audioDuration: number | undefined;

                if (hasValidAudio) {
                  const ext = (audioBlob.type || '').includes('mp4')
                    ? 'mp4'
                    : (audioBlob.type || '').includes('ogg')
                      ? 'ogg'
                      : 'webm';

                  audioFile = new File([audioBlob], `recording.${ext}`, {
                    type: audioBlob.type || 'audio/webm',
                  });

                  // Calculate audio duration for backend
                  let _audioCtx: AudioContext | undefined;
                  try {
                    const AudioCtx = (window.AudioContext ||
                      (window as any).webkitAudioContext) as typeof AudioContext;
                    _audioCtx = new AudioCtx();
                    if (_audioCtx.state === 'suspended') await _audioCtx.resume();
                    const buf = await audioBlob.arrayBuffer();
                    const decoded = await _audioCtx.decodeAudioData(buf);
                    audioDuration = Math.round(decoded.duration);
                  } catch {
                  } finally {
                    _audioCtx?.close().catch(() => {});
                  }
                }

                // Nothing to send: no valid audio, no text, no files
                if (!hasValidAudio && !text && (!files || files.length === 0)) {
                  showError('Recording was too short. Please try again.');
                  submittingRef.current = false;
                  setIsSending(false);
                  return;
                }

                try {
                  setIsConversationOpen(true);
                  requestScrollToBottom();
                  if (isFullscreen) exitFullscreen();

                  await sendMessage(text, files, audioFile, false, {
                    ...(hasValidAudio && !text ? { transcribePending: true } : {}),
                    ...(audioDuration != null && { audioDuration }),
                  });
                  closeRecordDock();
                } catch (e) {
                  showError(getUserFriendlyErrorMessage(e));
                } finally {
                  submittingRef.current = false;
                  setIsSending(false);
                }
              }}
            />
          </motion.div>
        )}

        {(dockMode === 'idle' || dockMode === 'fullscreen') && (
          <motion.div key="dock-idle" {...dockSlide}>
            {/* Crossfade between idle dock and recording dock in-place */}
            <div style={{ position: 'relative' }}>
              <div style={{
                opacity: isRecordingPhase ? 0 : 1,
                pointerEvents: isRecordingPhase ? 'none' : 'auto',
                transition: 'opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <Dock
                  ref={dockRef}
                  showConversation={showConversation}
                  onStartRecording={openRecordDock}
                  onEnterFullscreen={enterFullscreen}
                  onOpenConversation={() => { setIsConversationOpen(true); requestScrollToBottom(); }}
                  disabled={isSending}
                  onError={showError}
                  onSubmitMessage={async (text: string, files?: File[]) => {
                    if (submittingRef.current) return;
                    submittingRef.current = true;
                    setIsSending(true);

                    setIsConversationOpen(true);
                    requestScrollToBottom();
                    dockRef.current?.closeTypeImmediate();

                    try {
                      await sendMessage(text, files);
                    } catch (e) {
                      showError(getUserFriendlyErrorMessage(e));
                    } finally {
                      submittingRef.current = false;
                      setIsSending(false);
                    }
                  }}
                  onSaveNote={async (content: string) => {
                    try {
                      const lines = content.split('\n');
                      const title = (lines[0] ?? '').trim() || 'Untitled';
                      const body = lines.slice(1).join('\n').trim() || ' ';
                      await userNotesApi.createNote({
                        title: title.slice(0, 200),
                        content: body,
                      });
                      invalidateNotesCache();
                      showSuccess('Saved to My notes');
                    } catch (error) {
                      console.error('Failed to save note:', error);
                      showError('Failed to save note. Please try again.');
                    }
                  }}
                  onAttachFile={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.accept = FILE_ACCEPT;
                    input.onchange = (e) => {
                      const raw = (e.target as HTMLInputElement).files;
                      if (!raw || raw.length === 0) return;
                      const { accepted, rejected } = validateFiles(Array.from(raw));
                      const finalAccepted: File[] = [];
                      let runningSize = 0;
                      for (const file of accepted) {
                        if (runningSize + file.size > MAX_TOTAL_FILE_SIZE) {
                          rejected.push(`"${file.name}" exceeds ${formatFileSize(MAX_TOTAL_FILE_SIZE)} total limit`);
                          continue;
                        }
                        finalAccepted.push(file);
                        runningSize += file.size;
                      }
                      if (rejected.length > 0) {
                        showError(rejected.join('. '));
                      }
                      if (finalAccepted.length > 0) {
                        setAttachmentData({
                          type: 'file',
                          data: finalAccepted.length === 1 ? finalAccepted[0] : finalAccepted,
                        });
                        setShowAttachmentPreviewModal(true);
                      }
                    };
                    input.click();
                  }}
                  onAttachPhoto={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment';
                    input.onchange = (e) => {
                      const files = (e.target as HTMLInputElement).files;
                      if (files && files.length > 0) {
                        setAttachmentData({
                          type: 'photo',
                          data: files[0],
                        });
                        setShowAttachmentPreviewModal(true);
                      }
                    };
                    input.click();
                  }}
                />
              </div>
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                opacity: isRecordingPhase ? 1 : 0,
                pointerEvents: isRecordingPhase ? 'auto' : 'none',
                transition: 'opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <FullScreenRecordingDock
                  ctaRef={ctaRef}
                  isPaused={isPaused}
                  onStopTap={handleStopFromFullscreenDock}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      <AttachmentPreviewModal
        isOpen={showAttachmentPreviewModal}
        onClose={() => setShowAttachmentPreviewModal(false)}
        attachmentData={attachmentData}
        onSend={async (data, contextText) => {
          if (submittingRef.current) return;
          submittingRef.current = true;
          setIsSending(true);

          let files: File[] | undefined;
          let text = contextText || '';

          if (data.type === 'file') {
            files = Array.isArray(data.data) ? data.data : [data.data as File];
          } else if (data.type === 'photo') {
            files = [data.data as File];
          } else if (data.type === 'link') {
            text = data.data as string;
          }

          try {
            setIsConversationOpen(true);
            requestScrollToBottom();

            await sendMessage(text, files);

            setShowAttachmentPreviewModal(false);
            setAttachmentData(null);

            if (isFullscreen) exitFullscreen();
          } catch (e) {
            showError(getUserFriendlyErrorMessage(e));
          } finally {
            submittingRef.current = false;
            setIsSending(false);
          }
        }}
        onAddMoreFiles={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = FILE_ACCEPT;
          input.onchange = (e) => {
            const raw = (e.target as HTMLInputElement).files;
            if (!raw || raw.length === 0 || !attachmentData) return;
            const { accepted, rejected } = validateFiles(Array.from(raw));

            // Gather existing files for duplicate + cumulative checks
            const existingFiles: File[] = !attachmentData
              ? []
              : attachmentData.type === 'file'
                ? Array.isArray(attachmentData.data) ? attachmentData.data : [attachmentData.data as File]
                : [attachmentData.data as File];

            const currentSize = existingFiles.reduce((sum, f) => sum + f.size, 0);
            const finalAccepted: File[] = [];
            let runningSize = currentSize;

            for (const file of accepted) {
              const isDuplicate =
                existingFiles.some((f) => f.name === file.name && f.size === file.size) ||
                finalAccepted.some((f) => f.name === file.name && f.size === file.size);
              if (isDuplicate) {
                rejected.push(`"${file.name}" is already attached`);
                continue;
              }
              if (runningSize + file.size > MAX_TOTAL_FILE_SIZE) {
                rejected.push(`"${file.name}" exceeds ${formatFileSize(MAX_TOTAL_FILE_SIZE)} total limit`);
                continue;
              }
              finalAccepted.push(file);
              runningSize += file.size;
            }

            if (rejected.length > 0) {
              showError(rejected.join('. '));
            }
            if (finalAccepted.length === 0) return;
            setAttachmentData({
              type: 'file',
              data: [...existingFiles, ...finalAccepted],
            });
          };
          input.click();
        }}
      />
      </ChatBody>
      {/* ✅ Recording safety: discard confirmation modal — outside ChatBody to avoid z-index clipping */}
      <ModalOverlay visible={showDiscardModal} onClick={handleDiscardCancel}>
        <ModalContent visible={showDiscardModal} onClick={(e) => e.stopPropagation()}>
          <RerecordConfirmation className={showDiscardModal ? 'visible' : ''}>
            <DiscardAndReRecordTitleSpan>Discard recording?</DiscardAndReRecordTitleSpan>
            <DiscardAndReRecordDescriptionSpan>
              Your current recording will be lost if you navigate away.
            </DiscardAndReRecordDescriptionSpan>

            <CTAWrapper>
              <ModalCancelButton onClick={handleDiscardCancel}>Keep Recording</ModalCancelButton>
              <ReRecordButton onClick={handleDiscardConfirm}>Discard</ReRecordButton>
            </CTAWrapper>
          </RerecordConfirmation>
        </ModalContent>
      </ModalOverlay>
    </ChatWrapper>
  );
};
